/**
 * JobDescriptionContext
 *
 * Lets proposal section renderers (Key Personnel, Management Approach) offer a
 * "Generate / View Job Description" action ONLY inside the on-screen previewer
 * (no provider in the export path). Saved JDs are recalled and editable; new ones
 * are generated. Persistence is per (profile, solicitation, role).
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { JobDescriptionDialog } from './JobDescriptionDialog';
import {
  contractKey, roleKeyFromTitle, loadSavedRoleKeys, loadSavedJobDescription, saveJobDescription,
} from '../../services/jobDescriptionStore';
import type { JobDescription } from '../../types/job-description';

interface JobDescriptionLauncher {
  available: boolean;
  open: (role: any) => void;
  hasSaved: (role: any) => boolean;
}

const JobDescriptionCtx = createContext<JobDescriptionLauncher | null>(null);

export function useJobDescriptionLauncher(): JobDescriptionLauncher | null {
  return useContext(JobDescriptionCtx);
}

const titleOf = (r: any): string => r?.role || r?.title || r?.positionTitle || 'Role';

export function JobDescriptionProvider({
  contract,
  profile,
  sow,
  proposalContent,
  children,
}: {
  contract?: any;
  profile?: any;
  sow?: any;
  proposalContent?: any;
  children: React.ReactNode;
}) {
  const [active, setActive] = useState<{ role: any; savedJd: JobDescription | null } | null>(null);
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());

  const available = Boolean(contract && profile);
  const pid = profile?.id;
  const ck = contractKey(contract);

  useEffect(() => {
    let on = true;
    if (available && pid && ck) {
      loadSavedRoleKeys(pid, ck).then((s) => { if (on) setSavedKeys(s); });
    }
    return () => { on = false; };
  }, [available, pid, ck]);

  const open = useCallback(async (role: any) => {
    if (!available) return;
    const key = roleKeyFromTitle(titleOf(role));
    let savedJd: JobDescription | null = null;
    if (savedKeys.has(key)) savedJd = await loadSavedJobDescription(pid, ck, key);
    setActive({ role, savedJd });
  }, [available, savedKeys, pid, ck]);

  const hasSaved = useCallback(
    (role: any) => savedKeys.has(roleKeyFromTitle(titleOf(role))),
    [savedKeys]
  );

  const onSave = useCallback(async (jd: JobDescription) => {
    const role = active?.role;
    if (!role) return;
    const key = roleKeyFromTitle(titleOf(role));
    await saveJobDescription({ profileId: pid, noticeId: ck, roleKey: key, roleTitle: titleOf(role), jd });
    setSavedKeys((prev) => new Set(prev).add(key));
  }, [active, pid, ck]);

  return (
    <JobDescriptionCtx.Provider value={{ available, open, hasSaved }}>
      {children}
      {active && contract && profile && (
        <JobDescriptionDialog
          role={active.role}
          contract={contract}
          sow={sow}
          profile={profile}
          proposalContent={proposalContent}
          savedJd={active.savedJd}
          onSave={onSave}
          onClose={() => setActive(null)}
        />
      )}
    </JobDescriptionCtx.Provider>
  );
}

export default JobDescriptionProvider;
